import { EDocumentStatus, EFinancingProgram } from '@if/core/types'
import { BodyText, useCan } from '@if/mui'
import { Card, CardContent, CardHeader, Divider, Stack } from '@mui/material'
import { access } from '@src/access'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { Dispatch } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentRow from './DocumentRow'
import { IdentityVerificationByApplicant } from './documentTableRow-hooks'
import SendUploadRequestButton from './SendUploadRequestButton'

interface Props {
  requiredDocuments: RequiredDocument[]
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  lastSentOn: Date | null
  setError: Dispatch<Error>
  canEdit: boolean
  worksheet: FilteredWorksheet | null
  identityVerificationByApplicant: IdentityVerificationByApplicant
  isMerchantDashboardOrigin: boolean
}

const DocumentTable = ({
  requiredDocuments,
  creditApplicationId,
  financingProgramId,
  lastSentOn,
  setError,
  canEdit,
  worksheet,
  identityVerificationByApplicant,
  isMerchantDashboardOrigin,
}: Props) => {
  const { t } = useTranslation()
  const canRequestDocs = useCan(access.requestDocuments, { financingProgramId })
  const hasMissingDocs = requiredDocuments.some((doc) =>
    [EDocumentStatus.AwaitingDocument, EDocumentStatus.Incomplete, EDocumentStatus.Refused].includes(doc.status),
  )
  const receivedCount = requiredDocuments.filter((doc) => doc.receivedOn != null).length

  return (
    <Card>
      <CardHeader
        title={t('documentSectionHeaders.title')}
        subheader={t('documentSectionHeaders.receivedCount', {
          received: receivedCount,
          total: requiredDocuments.length,
        })}
        action={
          hasMissingDocs && canRequestDocs ? (
            <SendUploadRequestButton
              creditApplicationId={creditApplicationId}
              financingProgramId={financingProgramId}
              lastSentOn={lastSentOn}
              setError={setError}
            />
          ) : undefined
        }
      />
      <CardContent>
        {requiredDocuments.length === 0 ? (
          <BodyText>{t('common.notRequiredDocuments')}</BodyText>
        ) : (
          <Stack divider={<Divider />} spacing={2}>
            {requiredDocuments.map((doc) => (
              <DocumentRow
                key={doc.typeId}
                documentRequired={doc}
                creditApplicationId={creditApplicationId}
                financingProgramId={financingProgramId}
                setError={setError}
                canEdit={canEdit}
                worksheet={worksheet}
                identityVerification={identityVerificationByApplicant[doc.applicantType] ?? null}
                isMerchantDashboardOrigin={isMerchantDashboardOrigin}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

export default DocumentTable
